import { setupAmelioration } from '../..';
import {
  Amelioration,
  AmeliorationArgs,
  AmeliorationDialogContent,
  TransformationResult,
} from '../../ameliorationTypes';
import { TacModel } from '../../../../state/tac/tacTypes';
import { translate } from '../../../L10n';
import { t } from '../../../../translations';
import _ from 'lodash';
import { isInsert, isSection } from '../../../products';
import { getCheapestFittingShelf } from '../../../products/elvarli';
import {
  getAllSubItemInserts,
  getStabilityErrors,
} from '../../../../state/tac/tacReducer/validate/elvarli';
import productService from '../../../../services/products';
import tacHelpers from '../../../../state/tac/tacHelpers';
import addItem from '../../../../state/tac/tacReducer/addItem';
import { TacItem } from '../../../../generalTypes';
import removeItem from '../../../../state/tac/tacReducer/removeItem';

let elvarliStabilityAmelioration: Amelioration | null = null;

type Zone = {
  min: number;
  max: number;
  valid: boolean;
  openSlots: any[];
};

const getOpenSlotsBasedOnZone = (zone: Zone, openSlots: any) => {
  const withinZone = (slot: { y: number }, zone: Zone) =>
    slot.y >= zone.min && slot.y <= zone.max;
  return openSlots.filter((slot: { y: number }) => withinZone(slot, zone));
};

const getOpenSlotsBasedOnSection = (
  tac: TacModel,
  section: TacItem,
  insert: TacItem
) => {
  const tacWithOnlyRelevantSection = {
    ...tac,
    items: tac.items.filter(item => item.itemid === section.itemid),
  };
  return tacHelpers.getOpenSlots(tacWithOnlyRelevantSection, insert);
};

const getPreferredOpenSlot = (section: TacItem, openSlots: any) => {
  if (!openSlots.length) return null;

  const shortestDistanceToTopOrBottom = (slot: any) => {
    const distanceToTop = section.height - (slot.y + slot.height);
    const distanceToBottom = slot.y;

    return Math.min(distanceToTop, distanceToBottom);
  };
  const slotsSortedByPreference = openSlots.sort(
    (slot1: any, slot2: any) =>
      shortestDistanceToTopOrBottom(slot1) -
      shortestDistanceToTopOrBottom(slot2)
  );

  return slotsSortedByPreference[0];
};

const addStabilityInsertToOneOfOpenSlots = (
  tacModel: TacModel,
  section: TacItem,
  insert: TacItem,
  openSlots: any
) => {
  if (!openSlots.length) return false;
  const chosenSlot = getPreferredOpenSlot(section, openSlots);
  const shelfWithModifiedPos = { ...chosenSlot.local, ...insert };
  return addItem(tacModel, shelfWithModifiedPos, section, {
    isPersistent: true,
  });
};

const findUnstableZonesFrom = (stabilityErrors: any) => {
  return stabilityErrors.flatMap((error: any) =>
    error.zones.flatMap((zone: any) => zone)
  );
};

const findSectionInsertsWithinZone = (section: TacItem, zone: Zone) => {
  const inserts = section.items.filter(isInsert);
  const withinZone = (insert: TacItem) =>
    insert.y >= zone.min && insert.y <= zone.max;
  return inserts.filter(withinZone);
};

const fixElvarliSectionStability = (tacIn: TacModel): TransformationResult => {
  let missingReplacementShelvesEntirely = false;
  let tacOut = _.cloneDeep(tacIn);

  const sections = tacOut.items.filter(item => isSection(item));

  sections.forEach(section => {
    const inserts = section.items.filter(productService.isInsert);
    const insertsIncludingSubInserts = inserts
      .reduce(
        (acc: any, insert: any) => [...acc, getAllSubItemInserts(insert)],
        []
      )
      .flat();
    const stabilityErrors = getStabilityErrors(
      section,
      insertsIncludingSubInserts
    );
    const insert = getCheapestFittingShelf(section) as TacItem;
    if (!insert) {
      missingReplacementShelvesEntirely = true;
      return;
    }
    if (stabilityErrors.length) {
      const unstableZones = findUnstableZonesFrom(stabilityErrors);

      const openSlotsBasedOnSection = getOpenSlotsBasedOnSection(
        tacOut,
        section,
        insert
      );
      const openSlotsBasedOnUnstableZone = unstableZones.flatMap(
        (zone: Zone) => {
          return {
            ...zone,
            openSlots: getOpenSlotsBasedOnZone(zone, openSlotsBasedOnSection),
          };
        }
      );
      // Iterates through each unstable zone and adds a stability insert to one of the open slots
      // so that the section becomes stable.

      tacOut = openSlotsBasedOnUnstableZone.reduce(
        (acc: TacModel, zone: Zone) => {
          const modifiedSection = acc.items.find(
            (item: TacItem) => item.itemid === section.itemid
          ) as TacItem;
          let modifiedTac = addStabilityInsertToOneOfOpenSlots(
            acc,
            modifiedSection,
            insert,
            zone.openSlots
          );
          if (!modifiedTac) {
            // The zone is still unstable and no insert was possible to add to the zone.
            // This means that the zone is occupied with inserts that do not give stability points.
            // Find the inserts covering that specific zone.
            // eslint-disable-next-line no-unused-vars
            const inserts = findSectionInsertsWithinZone(
              section,
              zone
            ).reverse();
            const firstInsert = inserts[0];
            const replacingInsert = getCheapestFittingShelf(section) as TacItem;
            // Remove that insert from the tac.
            tacOut = removeItem(tacOut, firstInsert);
            const modifiedSection = tacOut.items.find(
              item => item.itemid === section.itemid
            ) as TacItem;
            // Get all open slots in the section
            const openSlots = getOpenSlotsBasedOnSection(
              tacOut,
              modifiedSection,
              replacingInsert
            );
            // Now there should be an open slot for the replacing insert
            const relatedOpenSlots = getOpenSlotsBasedOnZone(zone, openSlots);
            // Add the replacing insert to the now open slot
            modifiedTac = addStabilityInsertToOneOfOpenSlots(
              acc,
              modifiedSection,
              replacingInsert,
              relatedOpenSlots
            );
          }
          return modifiedTac ? modifiedTac : acc;
        },
        tacOut
      );
    }
  });

  if (missingReplacementShelvesEntirely) {
    return {
      success: false,
    };
  }

  const successMessage = translate(t.TOAST_ADD_SHELVES);

  const successResult: TransformationResult = {
    success: true,
    tacOut: tacOut,
    successMessage,
  };

  return successResult;
};

export const getElvarliStabilityAmelioration = (): Amelioration => {
  if (elvarliStabilityAmelioration) return elvarliStabilityAmelioration;

  const dialogContent: AmeliorationDialogContent = {
    headerText: translate(t.POPUP_ADD_SHELVES_HEADER),
    bodyText: translate(t.POPUP_ADD_SHELVES_BODY),
    acceptText: translate(t.YES_PLEASE),
    declineText: translate(t.PLANNER_SURVEY_DECLINE),
  };

  const args: AmeliorationArgs = {
    transform: fixElvarliSectionStability,
    dialogContent,
  };

  elvarliStabilityAmelioration = setupAmelioration(args);

  return elvarliStabilityAmelioration;
};
